import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import CommandPalette from "@/components/CommandPalette";
import TopBar from "@/components/Topbar";
import Sidebar from "@/components/Sidebar";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import MfaVerify from "@/pages/MfaVerify";
import PasswordChange from "@/pages/PasswordChange";
import SecuritySettings from "@/pages/SecuritySettings";
import NotificationPreferences from "@/pages/NotificationPreferences";
import Compliance from "@/pages/Compliance";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import TeamMembers from "@/pages/TeamMembers";
import AuditLogs from "@/pages/AuditLogs";
import Notifications from "@/pages/Notifications";
import HeadcountSettings from "@/pages/HeadcountSettings";
import Dashboard from "@/pages/Dashboard";
import Insights from "@/pages/Insights";
import AccessibilityStatement from "@/pages/AccessibilityStatement";
import GlobalFooter from "@/components/GlobalFooter";

// Phase 7a — Admin Workflow Builder
import AdminLayout from "@/pages/admin/AdminLayout";
import Taxonomy from "@/pages/admin/Taxonomy";
import ServiceLines from "@/pages/admin/ServiceLines";
import HRPartnersAdmin from "@/pages/admin/HRPartnersAdmin";
import Workflow from "@/pages/admin/Workflow";
import NotificationsAdmin from "@/pages/admin/NotificationsAdmin";
import DocCategories from "@/pages/admin/DocCategories";
import CaseTemplatesAdmin from "@/pages/admin/CaseTemplatesAdmin";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import RetentionAdmin from "@/pages/admin/RetentionAdmin";
// Phase 17 — Onboarding Pack
import EmailOutbox from "@/pages/admin/EmailOutbox";
import Runbook from "@/pages/admin/Runbook";
// Phase 18 — Document Management
import AdminDocuments from "@/pages/admin/AdminDocuments";

function AuthenticatedShell({ children }) {
  const [unread, setUnread] = React.useState(0);
  return (
    <div
      className="flex min-h-screen w-full"
      style={{
        backgroundColor: "var(--page-bg)",
        color: "var(--page-fg)",
      }}
    >
      <a
        href="#main-content"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:px-3 focus:py-2 focus:text-white"
        style={{ backgroundColor: "#1E3A5F" }}
      >
        Skip to main content
      </a>
      <Sidebar unreadCount={unread} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onUnreadChange={setUnread} />
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-x-hidden">
          {children}
        </main>
        <GlobalFooter />
      </div>
    </div>
  );
}

/**
 * Phase 7a — gate the entire authenticated app behind a forced password
 * change when the user logs in with a temporary password issued by an admin.
 * Any route except /change-password is locked until they update their password.
 */
function MustChangePasswordGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (
    user &&
    user.must_change_password === true &&
    location.pathname !== "/change-password"
  ) {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

// Phase 12 — only mount the global Cmd+K palette when authenticated
function ProtectedCommandPalette() {
  const { user } = useAuth();
  if (!user) return null;
  return <CommandPalette />;
}


function Shell({ children }) {
  const { user } = useAuth();
  if (!user) return <>{children}</>;
  // No chrome on the change-password screen — full-page focused flow.
  return (
    <MustChangePasswordGate>
      <ChromeIfNotPasswordChange>{children}</ChromeIfNotPasswordChange>
    </MustChangePasswordGate>
  );
}

function ChromeIfNotPasswordChange({ children }) {
  const location = useLocation();
  if (location.pathname === "/change-password") {
    return <>{children}</>;
  }
  return <AuthenticatedShell>{children}</AuthenticatedShell>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/mfa-verify" element={<MfaVerify />} />
      <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <PasswordChange />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <Insights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <Cases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:id"
        element={
          <ProtectedRoute>
            <CaseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-members"
        element={
          <ProtectedRoute>
            <TeamMembers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute roles={["hr", "compliance", "legal_review"]}>
            <AuditLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/headcount"
        element={
          <ProtectedRoute roles={["hr", "leadership"]}>
            <HeadcountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/security"
        element={
          <ProtectedRoute>
            <SecuritySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/preferences"
        element={
          <ProtectedRoute>
            <NotificationPreferences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute roles={["compliance", "legal_review"]}>
            <Compliance />
          </ProtectedRoute>
        }
      />

      {/* Phase 7a — Admin Workflow Builder
          AdminLayout itself enforces is_admin or compliance read-only and
          redirects unauthorised roles to /cases. */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["hr", "compliance"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/taxonomy" replace />} />
        <Route path="taxonomy" element={<Taxonomy />} />
        <Route path="service-lines" element={<ServiceLines />} />
        <Route path="hr-partners" element={<HRPartnersAdmin />} />
        <Route path="workflow" element={<Workflow />} />
        <Route path="notifications" element={<NotificationsAdmin />} />
        <Route path="doc-categories" element={<DocCategories />} />
        <Route path="templates" element={<CaseTemplatesAdmin />} />
        <Route path="users" element={<UsersAdmin />} />
        <Route path="retention" element={<RetentionAdmin />} />
        <Route path="email-outbox" element={<EmailOutbox />} />
        <Route path="runbook" element={<Runbook />} />
        <Route path="documents" element={<AdminDocuments />} />
      </Route>

      <Route path="/" element={<Navigate to="/cases" replace />} />
      <Route path="*" element={<Navigate to="/cases" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Shell>
            <AppRoutes />
          </Shell>
          <ProtectedCommandPalette />
          <ThemedToaster />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function ThemedToaster() {
  // Phase 12.2 — Sonner toaster needs to switch theme so its `richColors`
  // error/success palettes also pass AA in dark mode. Without this the toaster
  // stays in light theme and renders red-on-pink even when our app is dark.
  const { resolved } = useTheme();
  return (
    <Toaster
      position="top-right"
      richColors
      closeButton
      theme={resolved === "dark" ? "dark" : "light"}
      toastOptions={{ style: { borderRadius: "0.75rem" } }}
    />
  );
}
